import React from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  HotelStarRatingWithoutNotAvailable,
} from "@b2bportal/core-types";
import {
  toStarRating,
  toStarRatingForAvailableRating,
} from "@b2bportal/core-utilities";
import type { HotelStarRating } from "@b2bportal/lodging-api";
import {
  CheckboxButton,
  CheckboxGroup,
  type ChildCheckboxComponentProps,
  Icon,
} from "@tenant/ui";
import defaultStyles from "./FilterRatingHorizontal.module.scss";

export type FilterRatingHorizontalProps = {
  selectedValues: HotelStarRating[];
  onChange: (values: HotelStarRating[]) => void;
};

const CheckboxElement = ({
  id,
  checked,
  onCheckedChange,
  label,
}: ChildCheckboxComponentProps) => {
  const styles = useHotelsStyles(
    CoreHotelsComponent.FilterRatingHorizontal,
    defaultStyles
  );
  const [, cn] = useModuleBEM(
    styles,
    CoreHotelsComponent.FilterRatingHorizontal
  );

  return (
    <CheckboxButton
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      className={cn("checkbox")}
    >
      {label}
    </CheckboxButton>
  );
};

export const FilterRatingHorizontal = ({
  selectedValues,
  onChange,
}: FilterRatingHorizontalProps) => {
  const styles = useHotelsStyles(
    CoreHotelsComponent.FilterRatingHorizontal,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreHotelsComponent.FilterRatingHorizontal
  );

  const getRatingLabel = (rating: HotelStarRating) => (
    <div className={cn("label")}>
      {toStarRating(rating)} <Icon iconName={IconNameEnum.starFilled} />
    </div>
  );

  const ratings = Object.values(HotelStarRatingWithoutNotAvailable).sort(
    (a, b) => {
      return (
        toStarRatingForAvailableRating(a) - toStarRatingForAvailableRating(b)
      );
    }
  );

  // CheckboxGroup expects string[] but we have HotelStarRating[] (enum)
  // Convert to strings for CheckboxGroup, then convert back on change
  const options = ratings.map((rating) => ({
    label: getRatingLabel(rating),
    value: String(rating),
  }));

  const selectedStrings = selectedValues.map(String);

  const handleChange = (stringValues: string[]) => {
    // Convert strings back to HotelStarRating enum values
    const enumValues = stringValues as HotelStarRating[];
    onChange(enumValues);
  };

  return (
    <div className={block}>
      <CheckboxGroup
        className={cn("checkbox-group")}
        options={options}
        checkedOptions={selectedStrings}
        onOptionsChange={handleChange}
        checkboxElement={CheckboxElement}
      />
    </div>
  );
};
